import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

interface RecipeIngredient {
  ingredientId: string | { _id: string; name: string; sku: string };
  quantity: number;
  unit: string;
}

interface Formula {
  _id: string;
  formulaCode: string;
  name: string;
  description?: string;
  ingredients: RecipeIngredient[];
  isDefault: boolean;
  priority: number;
  useCase?: string;
  isActive?: boolean;
}

interface Recipe {
  _id: string;
  menuItemId: string | { _id: string; name: { en: string; th: string }; sku: string };
  size: 'small' | 'medium' | 'large' | 'all';
  version: string;
  status: 'draft' | 'review' | 'published' | 'archived';
  formulas: Formula[];
  autoSwitchEnabled: boolean;
  notes?: string;
  createdBy: string | { _id: string; username: string };
  reviewedBy?: string | { _id: string; username: string };
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface RecipeVersion {
  version: string;
  status: string;
  createdAt: string;
  createdBy: string;
}

// Fetch all recipes with optional filters
export function useRecipes(menuItemId?: string, status?: string) {
  return useQuery({
    queryKey: ['recipes', menuItemId, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (menuItemId) params.append('menuItemId', menuItemId);
      if (status) params.append('status', status);

      const response = await apiClient.get<APIResponse<{ recipes: Recipe[] }>>(
        `/recipes?${params.toString()}`
      );
      return response.data.data.recipes;
    },
  });
}

// Fetch single recipe
export function useRecipe(id: string) {
  return useQuery({
    queryKey: ['recipe', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ recipe: Recipe }>>(
        `/recipes/${id}`
      );
      return response.data.data.recipe;
    },
    enabled: !!id,
  });
}

// Fetch recipe versions for a menu item
export function useRecipeVersions(menuItemId: string) {
  return useQuery({
    queryKey: ['recipe-versions', menuItemId],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ versions: RecipeVersion[] }>>(
        `/recipes/menu-item/${menuItemId}/versions`
      );
      return response.data.data.versions;
    },
    enabled: !!menuItemId,
  });
}

// Fetch published recipe for a menu item
export function usePublishedRecipe(menuItemId: string) {
  return useQuery({
    queryKey: ['published-recipe', menuItemId],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ recipe: Recipe }>>(
        `/recipes/menu-item/${menuItemId}/published`
      );
      return response.data.data.recipe;
    },
    enabled: !!menuItemId,
  });
}

export type { Recipe, Formula, RecipeIngredient, RecipeVersion };
